import { ApiAbortedError, handleApiError, RawResponseError } from '@/plugins/api/openapi/errors'
import router from '@/router'
import store from '@/store'
import { useAuthStore } from '@/store/pinia/auth'
import EventEmitter from 'eventemitter3'
import { FetchResponse } from 'openapi-fetch'
import { useV2Api } from '.'
import { usePending2FAStore } from '@/store/pinia/pending-2fa'

export class PromiseStoplight {
  private state: 'opened' | 'closed'
  private eventEmitter = new EventEmitter<{
    open: []
    close: []
  }>()

  get isOpened() {
    return this.state === 'opened'
  }

  get isClosed() {
    return this.state === 'closed'
  }

  constructor(initialState: typeof this.state = 'opened') {
    this.state = initialState
    this.eventEmitter.on('open', () => {
      this.state = 'opened'
    })
    this.eventEmitter.on('close', () => {
      this.state = 'closed'
    })
  }

  go<T>(fn: () => Promise<T>) {
    return new Promise<T>((resolve, reject) => {
      if (this.isOpened) {
        fn().then(resolve).catch(reject)
      } else {
        this.eventEmitter.on('open', () => {
          fn().then(resolve).catch(reject)
        })
      }
    })
  }

  open() {
    this.eventEmitter.emit('open')
  }

  close() {
    this.eventEmitter.emit('close')
  }
}

const refreshingTokenStoplight = new PromiseStoplight('opened')
let isLoggingOut = false

export async function wrapEndpoint<
  T extends Record<string | number, any>,
  Options,
  Media extends `${string}/${string}`,
>(
  endpoint: () => Promise<FetchResponse<T, Options, Media>>,
): Promise<NonNullable<FetchResponse<T, Options, Media>['data']>> {
  const res = await endpoint()

  if (res.response.ok) {
    return res.data!
  }

  const response = res.error as RawResponseError
  const api = useV2Api()
  const authStore = useAuthStore()
  const pending2FAStore = usePending2FAStore()

  if (response.statusCode === 401 && response.error.name === 'InvalidTokenException') {
    if (refreshingTokenStoplight.isClosed) {
      return await refreshingTokenStoplight.go(() => wrapEndpoint(endpoint))
    }

    refreshingTokenStoplight.close()
    const { token } = await api.auth.refresh()
    authStore.setToken(token)
    await store.dispatch('auth/setToken', token)
    setTimeout(() => {
      refreshingTokenStoplight.open()
    }, 100)

    return wrapEndpoint(endpoint)
  }

  if (response.statusCode === 401) {
    if (isLoggingOut) {
      throw new ApiAbortedError()
    }

    isLoggingOut = true
    if (authStore.session) {
      await api.auth.logoutSession(authStore.session!.session.id).catch(() => {})
      await store.dispatch('auth/clearCredentials', null, { root: true })
      authStore.setToken(null)
    } else if (pending2FAStore.token) {
      pending2FAStore.token = null
    }
    setTimeout(async () => {
      await router.push('/login')
      isLoggingOut = false
    }, 100)
  }

  throw handleApiError(response)
}
